import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { parseContributingCenters } from '../utils/dataProcessor';

const LABEL_STYLE = { fontSize: 12, fill: '#374151', fontWeight: 500 };

export function CentersModal({ isOpen, onClose, filteredResults }) {
  const [tip, setTip] = useState('');
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  const handleKeyDown = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [isOpen, handleKeyDown]);

  const chartData = useMemo(() => {
    if (!filteredResults?.length) return [];
    const centerMap = parseContributingCenters(filteredResults);
    return [...centerMap.values()]
      .sort((a, b) => b.resultCount - a.resultCount)
      .map(c => ({
        name: c.code.length > 20 ? c.code.substring(0, 18) + '...' : c.code,
        fullName: c.fullName || c.code,
        resultCount: c.resultCount,
      }));
  }, [filteredResults]);

  if (!isOpen) return null;

  const renderLabel = (props) => {
    const { x, y, width, height, index } = props;
    const entry = chartData[index];
    if (!entry) return null;
    return (
      <text x={x + width + 4} y={y + height / 2} {...LABEL_STYLE} dominantBaseline="middle">
        {entry.resultCount}
      </text>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex flex-col w-full max-w-2xl max-h-[85vh] bg-[var(--surface-primary)] rounded-xl shadow-2xl overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-primary)]">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Centers ({chartData.length})</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-tertiary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary */}
        <div className="px-5 py-3 border-b border-[var(--border-primary)]">
          <p className="text-sm font-semibold text-[var(--text-primary)]">{chartData.length} unique center{chartData.length !== 1 ? 's' : ''}</p>
          <p className="text-xs text-[var(--text-tertiary)] mt-0.5">Bars represent the number of individual results where each center has been listed.</p>
        </div>

        {/* Chart */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {chartData.length === 0 ? (
            <p className="text-sm text-[var(--text-tertiary)] text-center py-8">No center data available for the current selection.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 32)}>
              <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={140} interval={0}
                  tick={({ x, y, payload }) => {
                    const entry = chartData.find(d => d.name === payload.value);
                    return (
                      <g transform={`translate(${x},${y})`}
                        style={{ cursor: 'default' }}
                        onMouseEnter={(e) => { setTip(entry?.fullName || payload.value); setTipPos({ x: e.clientX, y: e.clientY }); }}
                        onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                        onMouseLeave={() => setTip('')}
                      >
                        <text x={-4} y={0} dy={4} textAnchor="end" fontSize={11} fill="#666">{payload.value}</text>
                      </g>
                    );
                  }}
                />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.6 }}>
                      <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{d.fullName}</div>
                      <div style={{ color: '#4b5563' }}>Results: <strong>{d.resultCount.toLocaleString()}</strong></div>
                    </div>
                  );
                }} />
                <Bar dataKey="resultCount" fill="#14b8a6" radius={[0, 4, 4, 0]}>
                  <LabelList content={renderLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          {tip && (
            <div className="fixed z-50 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none"
                 style={{ left: tipPos.x + 12, top: tipPos.y - 28 }}>
              {tip}
            </div>
          )}
        </div>

      </div>
    </div>,
    document.body,
  );
}
